import type { ChatMessage, LlmProvider, ToolSpec } from '@rally/llm';
import { sanitizeForSpeech } from '@rally/core';
import type { AgentEvent, ToolClient, ToolInfo, TurnResult } from './types.ts';

/**
 * LLM planner: a standard tool-use loop over the MCP tools, tuned for voice.
 *
 * Two latency tricks keep turns short enough to speak:
 * 1. Speech passthrough: Rally tools return a ready-to-say `speech`
 *    field. When every tool called in a step is marked passthrough and
 *    returned speech, the agent speaks it directly and skips the second model
 *    call (typically the slowest part of a voice turn).
 * 2. A small tool budget per turn (max 6 steps), so a confused model fails
 *    fast with a clarifying question instead of looping.
 */

export const VOICE_SYSTEM_PROMPT = `You are Alexa, a warm, efficient voice assistant on an Echo Show in the customer's kitchen. You help them get people together through the Rally add-on's tools.

Voice rules:
- Your reply is spoken aloud. One to three short sentences. No lists longer than three, no markdown, no emoji, no URLs.
- Prefer the "speech" field from tool results; you may shorten it but never change facts, names, dates or confirmation codes.
- Ask at most one question at a time.

Rally rules:
- To start, call start_plan with what they said (title, invite list, when, honoree, surprise). It only drafts. Read it back.
- Call send_invites, nudge_guests, book_place, change_plan, message_guests, cancel_plan and forget only after the customer clearly agreed. If a tool (including remember) says needs_confirmation, ask, and on yes call it again with confirm true and the result's confirmation value. When a result proposes a next step (next_step with next_confirmation), a yes to that offer means calling it with confirm true and that next_confirmation.
- "How is it going" means plan_status; offer its suggested next step. "What am I doing" means whats_planned; if it returns a secrecy line, say exactly that line and nothing else.
- Use find_places before book_place. Never say who needs what; speak only the reasons tools give.
- Never record someone else's diet, allergies, access or health; the remember tool handles that.
- If the customer says they're in a hurry or asks for the short version, answer in one sentence plus any question, but keep every read-back before a commitment complete. Judge that only from their words, never from how they sound.
- "Like last time" or "the usual" means start_plan with like_last_time true; recall answers what Rally knows about someone or a circle's usual.
{{skill}}
Current local time: {{now}}.`;

const DEFAULT_PASSTHROUGH = new Set([
  'start_plan',
  'send_invites',
  'plan_status',
  'nudge_guests',
  'find_places',
  'book_place',
  'change_plan',
  'message_guests',
  'whats_planned',
  'remember',
  'recall',
  'forget',
  'cancel_plan',
]);

export class LlmVoiceAgent {
  readonly #llm: LlmProvider;
  readonly #tools: ToolClient;
  readonly #history: ChatMessage[] = [];
  readonly #passthrough: Set<string>;
  #toolSpecs?: ToolSpec[];
  #toolInfo = new Map<string, ToolInfo>();
  readonly maxSteps: number;
  readonly systemPrompt: string;

  constructor(opts: { llm: LlmProvider; tools: ToolClient; systemPrompt?: string; passthrough?: string[]; maxSteps?: number; extraContext?: string; skill?: string }) {
    this.#llm = opts.llm;
    this.#tools = opts.tools;
    this.#passthrough = new Set(opts.passthrough ?? DEFAULT_PASSTHROUGH);
    this.maxSteps = opts.maxSteps ?? 6;
    this.systemPrompt =
      (opts.systemPrompt ?? VOICE_SYSTEM_PROMPT)
        .replace('{{now}}', new Date().toString())
        .replace('{{skill}}', opts.skill ? `\nRally Agent Skill (loaded at runtime):\n${opts.skill.slice(0, 6000)}\n` : '') + (opts.extraContext ? `\n${opts.extraContext}` : '');
  }

  get planner(): string {
    return `${this.#llm.name}:${this.#llm.model}`;
  }

  async #specs(): Promise<ToolSpec[]> {
    if (!this.#toolSpecs) {
      const tools = await this.#tools.listTools();
      tools.forEach((t) => this.#toolInfo.set(t.name, t));
      this.#toolSpecs = tools.map((t) => ({ name: t.name, description: t.description, inputSchema: t.inputSchema }));
    }
    return this.#toolSpecs;
  }

  async turn(utterance: string, onEvent: (e: AgentEvent) => void = () => {}): Promise<TurnResult> {
    const started = performance.now();
    const tools = await this.#specs();
    const calls: TurnResult['toolCalls'] = [];
    this.#history.push({ role: 'user', content: utterance });
    let speech = '';

    for (let step = 0; step < this.maxSteps; step++) {
      onEvent({ type: 'thinking', provider: this.#llm.name, model: this.#llm.model });
      const res = await this.#llm.chat({
        messages: [{ role: 'system', content: this.systemPrompt }, ...this.#history.slice(-24)],
        tools,
        temperature: 0.2,
        maxTokens: 600,
      });
      if (res.toolCalls.length === 0) {
        speech = sanitizeForSpeech(res.text) || 'Sorry, could you say that again?';
        this.#history.push({ role: 'assistant', content: speech });
        break;
      }
      this.#history.push({ role: 'assistant', content: res.text ?? '', toolCalls: res.toolCalls });
      const spoken: string[] = [];
      let allPassthrough = true;
      // Independent tool calls in one step run in parallel.
      const outcomes = await Promise.all(
        res.toolCalls.map(async (call) => {
          onEvent({ type: 'tool_call', id: call.id, name: call.name, args: call.arguments });
          const outcome = await this.#tools.callTool(call.name, call.arguments);
          onEvent({ type: 'tool_result', id: call.id, name: call.name, outcome });
          const ui = outcome.uiResourceUri ?? this.#toolInfo.get(call.name)?.uiResourceUri;
          if (ui && !outcome.isError) onEvent({ type: 'ui', resourceUri: ui, toolName: call.name, data: outcome.data });
          return { call, outcome };
        }),
      );
      for (const { call, outcome } of outcomes) {
        calls.push({ name: call.name, args: call.arguments, outcome });
        this.#history.push({ role: 'tool', toolCallId: call.id, name: call.name, content: compactForModel(outcome.data, outcome.text) });
        const s = typeof outcome.data?.speech === 'string' ? outcome.data.speech : undefined;
        if (!outcome.isError && s && this.#passthrough.has(call.name)) spoken.push(s);
        else allPassthrough = false;
      }
      if (allPassthrough && spoken.length) {
        speech = sanitizeForSpeech(spoken.join(' '));
        this.#history.push({ role: 'assistant', content: speech });
        break;
      }
      if (step === this.maxSteps - 1) speech = 'Sorry, I got a bit tangled up. Could you say that another way?';
    }

    onEvent({ type: 'speech', text: speech });
    const latencyMs = Math.round(performance.now() - started);
    onEvent({ type: 'done', latencyMs, steps: calls.length });
    return { speech, toolCalls: calls, latencyMs, planner: this.planner };
  }
}

/** Tool results go back to the model as compact JSON; UI-only fields are dropped to save context. */
export function compactForModel(data: Record<string, unknown> | undefined, text: string): string {
  if (!data) return text.slice(0, 4000);
  const { display: _display, ui: _ui, ...rest } = data as Record<string, unknown>;
  const json = JSON.stringify(rest);
  return json.length > 6000 ? `${json.slice(0, 6000)}…` : json;
}
